import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Movie } from '@/types/movie';
import { useFavorites } from '@/hooks/useFavorites';
import { formatCount, formatDuration, formatRating } from '@/utils/format';

interface HeroCarouselProps {
  movies: Movie[];
}

export default function HeroCarousel({ movies }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const { isFavorite, toggleFavorite } = useFavorites();
  const touchStartX = useRef<number | null>(null);

  const go = useCallback(
    (dir: number) => {
      setIndex((prev) => (prev + dir + movies.length) % movies.length);
    },
    [movies.length],
  );

  useEffect(() => {
    const timer = window.setInterval(() => go(1), 6500);
    return () => window.clearInterval(timer);
  }, [go]);

  if (movies.length === 0) return null;
  const active = movies[index];
  const favorited = isFavorite(active.id);

  return (
    <section
      className="relative w-full overflow-hidden"
      onTouchStart={(e) => {
        touchStartX.current = e.touches[0].clientX;
      }}
      onTouchEnd={(e) => {
        if (touchStartX.current === null) return;
        const delta = e.changedTouches[0].clientX - touchStartX.current;
        if (Math.abs(delta) > 45) go(delta < 0 ? 1 : -1);
        touchStartX.current = null;
      }}
    >
      <div className="absolute inset-0 h-full w-full">
        <img
          src={active.backdrop || active.poster}
          alt={`${active.title} 剧照`}
          className="h-full w-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/85 to-background-50/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-transparent to-background-50/60" />
      </div>

      <div className="relative mx-auto flex w-full flex-col gap-8 px-4 pb-12 pt-12 md:px-8 md:pb-16 md:pt-16 lg:flex-row lg:items-end lg:gap-12 lg:pb-20">
        <div className="w-full max-w-2xl animate-fade-up" key={active.id}>
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-white">
              <i className="ri-moon-fill"></i>
            </span>
            <div>
              <h1 className="font-display text-lg tracking-[0.2em] text-foreground-950">午夜小电影</h1>
              <p className="text-xs text-foreground-500">精选午夜主题短片与电影 · 为不睡的人整理片单</p>
            </div>
          </div>

          <div className="mb-3 flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-accent-500 px-3 py-1 text-xs font-medium text-background-950">
              本期焦点
            </span>
            <span className="rounded-full bg-secondary-100/80 px-3 py-1 text-xs text-secondary-900">
              {active.category}
            </span>
            <span className="flex items-center gap-1 rounded-full bg-background-200/70 px-3 py-1 text-xs text-foreground-700">
              <i className="ri-star-fill text-accent-500"></i>
              {formatRating(active.rating)} 分
            </span>
          </div>

          <h2 className="font-display text-4xl leading-tight text-foreground-950 md:text-6xl">{active.title}</h2>

          <p className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-foreground-500">
            <span>{active.year}</span>
            <span>{formatDuration(active.duration)}</span>
            <span>导演 {active.director}</span>
            <span className="flex items-center gap-1">
              <i className="ri-fire-line text-primary-400"></i>
              {formatCount(active.views)} 次观看
            </span>
          </p>

          <p className="mt-5 max-w-xl text-sm leading-relaxed text-foreground-600 md:text-base">{active.synopsis}</p>

          <div className="mt-7 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${active.id}`}
              className="flex h-11 items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 text-sm font-medium text-white transition hover:bg-primary-600"
            >
              <i className="ri-play-circle-line text-lg"></i>
              查看影片详情
            </Link>
            <button
              type="button"
              onClick={() => toggleFavorite(active.id)}
              className={`flex h-11 items-center gap-2 whitespace-nowrap rounded-md border px-5 text-sm transition ${
                favorited
                  ? 'border-primary-400 bg-primary-500/15 text-foreground-800'
                  : 'border-background-300/70 text-foreground-700 hover:border-primary-400 hover:text-foreground-950'
              }`}
            >
              <i className={favorited ? 'ri-heart-fill' : 'ri-heart-line'}></i>
              {favorited ? '已收藏' : '加入收藏'}
            </button>
          </div>
        </div>

        <div className="w-full lg:ml-auto lg:w-auto">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => go(-1)}
              className="flex h-10 w-10 items-center justify-center rounded-md border border-background-300/70 text-foreground-700 transition hover:text-foreground-950"
              aria-label="上一部"
            >
              <i className="ri-arrow-left-s-line text-xl"></i>
            </button>
            <button
              type="button"
              onClick={() => go(1)}
              className="flex h-10 w-10 items-center justify-center rounded-md border border-background-300/70 text-foreground-700 transition hover:text-foreground-950"
              aria-label="下一部"
            >
              <i className="ri-arrow-right-s-line text-xl"></i>
            </button>
            <div className="ml-2 flex items-center gap-1.5">
              {movies.map((m, i) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setIndex(i)}
                  aria-label={`切换到 ${m.title}`}
                  className={`h-1.5 rounded-full transition-all ${
                    i === index ? 'w-6 bg-primary-500' : 'w-1.5 bg-background-400'
                  }`}
                ></button>
              ))}
            </div>
          </div>

          <div className="mt-5 hidden gap-3 lg:flex">
            {movies.map((m, i) => (
              <button
                key={m.id}
                type="button"
                onClick={() => setIndex(i)}
                className={`group relative h-28 w-20 shrink-0 overflow-hidden rounded-md border transition ${
                  i === index ? 'border-primary-400' : 'border-background-300/50 opacity-70 hover:opacity-100'
                }`}
              >
                <img
                  src={m.poster}
                  alt={`${m.title} 海报`}
                  className="h-full w-full object-cover object-top"
                />
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}